import type { Circuit } from '@multirx/shared/types/track.js';
import { mountLazyCircuit3dPreview } from '../circuits/circuit3dLazyPreview.js';

export type CircuitPreviewCardLayout = 'row' | 'tile';

export interface CircuitPreviewCardLines {
  title: string;
  subtitle?: string;
  meta?: string;
  date?: string;
  dateTime?: string;
}

export interface CircuitPreviewCardOptions {
  layout?: CircuitPreviewCardLayout;
  lines: CircuitPreviewCardLines;
  href?: string;
  selected?: boolean;
  extraClass?: string;
  badgeClass?: string;
  badgeText?: string;
  ariaLabel?: string;
  onActivate?: () => void;
}

/** Carte liste avec aperçu 3D — mutualisée (Circuits, Replays, …). */
export function createCircuitPreviewCard(options: CircuitPreviewCardOptions): HTMLElement {
  const layout = options.layout ?? 'row';

  const card = options.href ? document.createElement('a') : document.createElement('article');

  const classes = ['circuit-card', `circuit-card--${layout}`];
  if (options.extraClass) classes.push(options.extraClass);
  if (options.selected) classes.push('circuit-card--selected');
  card.className = classes.join(' ');

  if (options.href) {
    (card as HTMLAnchorElement).href = options.href;
  }
  if (options.ariaLabel) {
    card.setAttribute('aria-label', options.ariaLabel);
  }

  const preview = document.createElement('div');
  preview.className = 'circuit-card-preview';
  preview.setAttribute('aria-hidden', 'true');

  if (layout === 'tile') {
    const previewWrap = document.createElement('div');
    previewWrap.className = 'circuit-card-preview-wrap';
    previewWrap.appendChild(preview);
    if (options.badgeText) {
      const badge = document.createElement('span');
      badge.className = options.badgeClass ?? 'circuit-card-badge';
      badge.textContent = options.badgeText;
      previewWrap.appendChild(badge);
    }
    card.appendChild(previewWrap);
  } else {
    card.appendChild(preview);
  }

  const info = document.createElement('div');
  info.className = 'circuit-card-info';

  const title = document.createElement('span');
  title.className = 'circuit-card-name';
  title.textContent = options.lines.title;
  info.appendChild(title);

  if (options.lines.subtitle) {
    const subtitle = document.createElement('span');
    subtitle.className = 'circuit-card-subtitle';
    subtitle.textContent = options.lines.subtitle;
    info.appendChild(subtitle);
  }

  if (options.lines.meta) {
    const meta = document.createElement('span');
    meta.className = 'circuit-card-params';
    meta.textContent = options.lines.meta;
    info.appendChild(meta);
  }

  if (options.lines.date) {
    const date = document.createElement('time');
    date.className = 'circuit-card-date';
    date.textContent = options.lines.date;
    if (options.lines.dateTime) date.setAttribute('datetime', options.lines.dateTime);
    info.appendChild(date);
  }

  card.appendChild(info);

  if (options.href && options.onActivate) {
    card.addEventListener('click', (e) => {
      e.preventDefault();
      options.onActivate?.();
    });
  } else if (options.onActivate) {
    card.addEventListener('click', () => options.onActivate?.());
    card.setAttribute('role', 'button');
    card.tabIndex = 0;
    card.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        options.onActivate?.();
      }
    });
  }

  return card;
}

export function getCircuitPreviewCardContainer(card: HTMLElement): HTMLElement | null {
  return card.querySelector('.circuit-card-preview');
}

/**
 * Monte une vignette 3D paresseuse dans la carte — retourne une fonction de
 * nettoyage à appeler avant de retirer/reconstruire la carte (voir
 * `mountLazyCircuit3dPreview`), sinon le contexte WebGL reste ouvert.
 */
export function paintCircuitPreviewCard(
  card: HTMLElement,
  circuit: Circuit | null | undefined,
  root?: Element | null,
  circuitName?: string | null
): () => void {
  const container = getCircuitPreviewCardContainer(card);
  if (!container) return () => {};
  const wrap = container.closest('.circuit-card-preview-wrap');
  if (!circuit?.path?.length) {
    wrap?.classList.add('circuit-card-preview-wrap--empty');
    return () => {};
  }
  wrap?.classList.remove('circuit-card-preview-wrap--empty');
  return mountLazyCircuit3dPreview(container, circuit, root, circuitName);
}
